import './style.css';
import './classic-battle.css';
import './classic-room.css';
import './classic-hall.css';
import './classic-tools.css';
import gameData from '../shared/game-data.json';
import { PROP_DEFINITIONS } from '../shared/engine';
import type { ClientCommand, GameState, PlayerState, PropId, ShotResult } from '../shared/types';
import { RoomConnection, savedNickname, type ConnectionStatus } from './network';
import { BattleRenderer, type GameAssets, type WeaponAsset } from './renderer';
import { classicStage, escapeHtml as escape } from './classic-view';
import { TurnTransition, turnKey, windArrowSample, turnGlowSample } from './turn-transition';
import { GameAudio } from './game-audio';
import { classicRoom } from './classic-room';
import classicUi from '../shared/classic-ui.json';
import { angleSector, angerSector, danderReadySample, gaugeAim, type RecordedAim } from './classic-gauge';
import { chargePower, chargeVisualPower } from './charge-power';
import { InterpolatedSprite } from './interpolated-sprite';
import { VisualTween } from './visual-tween';
import { BattleAssetLoader, roomAssetKey } from './battle-asset-loader';
import { ORIGINAL_WALK_STEP, walkCadence } from './movement-cadence';
import { AimCadence } from './aim-cadence';
import { classicHall, type RoomSummary } from './classic-hall';
import { validRoomId } from '../shared/rooms';
import { battleHudPlayers, sameControlTurn } from './turn-hud';
import { invitedRoomId, roomInviteUrl } from './room-navigation';
import { assetUrl } from './hd-assets';
import { ResourcePackLoader } from './resource-pack-loader';
import { refreshPackedAssetBindings } from './packed-asset-bindings';
import { deferPackedImages, packedAssetRevision } from './asset-delivery';
import packManifest from '../shared/resource-packs.json';
import { BattleResultPresentation, type BattleResultFrame } from './battle-result';

const rawData = gameData as unknown as Record<string, any>;
const assets: GameAssets = {
  ...rawData,
  maps: Array.isArray(rawData.maps) ? rawData.maps : Object.values(rawData.maps),
  weapons: Array.isArray(rawData.weapons) ? rawData.weapons : Object.values(rawData.weapons),
  characters: Array.isArray(rawData.characters) ? rawData.characters : Object.values(rawData.characters ?? {}),
};
const $ = <T extends HTMLElement = HTMLElement>(selector: string) => document.querySelector<T>(selector)!;
const app = $('#app');
let state: GameState | null = null;
let renderer: BattleRenderer | null = null;
let currentScreen = '';
let clockOffset = 0;
let angle = 45;
const visualAngle = new VisualTween(angle);
let power = 0;
let referencePower = 0;
let recordedPowerWidth = 0;
let previousPowerWidth = 0;
let presentedPowerTurn = '';
let charging = false;
let chargeStarted = 0;
let selectedProps: PropId[] = [];
let usePow = false;
let toastTimeout: ReturnType<typeof setTimeout>;
let connected = false;
let moveTick = 0;
let walkDirection = 1;
const aimCadence = new AimCadence();
let pendingAimSeq = -1;
let lastTurn = '';
let previousShotId = '';
let latestShot: ShotResult | null = null;
const resultPresentation = new BattleResultPresentation();
let presentedResult: BattleResultFrame | null = null;
let resultMovieKey = '';
const uiSprite = new InterpolatedSprite();
let announceResult = false;
let announcedResult = '';
let lastSelfAim: RecordedAim | null = null;
let presentedLifeRound = -1;
const presentedLife = new Map<string, boolean>();
let angerFrom = 0, angerTarget = 0, angerStarted = 0;
let muted = localStorage.getItem('ddt2009.muted') === 'true';
const keys = new Set<string>();
const blockedKeys = new Set<string>();
const pointerTurns = new WeakMap<HTMLElement, string>();
let hallNickname = '';
let hallRooms: RoomSummary[] = [];
let selectedRoom: { id: string; name: string } | null = null;
let hallTimer: ReturnType<typeof setInterval> | null = null;
let hallRequest = 0;
let hallLoading = false;
let hallCreating = false;
let hallError = '';
let hallDraft = '';
let invitationUsed = false;
let globalReady = false;
let globalPreload: Promise<void> | null = null;
let preloadFailed = false;
const resourcePacks = new ResourcePackLoader();
deferPackedImages(packManifest.packs.flatMap(pack => pack.entries.filter(entry => entry.type.startsWith('image/')).map(entry => entry.path)));
const gameAudio = new GameAudio(() => muted, resourcePacks);
const battleAssets = new BattleAssetLoader(assets, resourcePacks);
let entryLoadingFrame = 0;
let boundAssetRevision = -1;
function scheduleEntryLoading() {
  if (entryLoadingFrame) return;
  entryLoadingFrame = requestAnimationFrame(() => {
    entryLoadingFrame = 0;
    const revision = packedAssetRevision();
    if (revision !== boundAssetRevision) { boundAssetRevision = revision; refreshPackedAssetBindings(); }
    updateEntryLoading();
  });
}
resourcePacks.onProgress = scheduleEntryLoading;
battleAssets.onReady = (key) => {
  if (state?.phase === 'lobby' && key === roomAssetKey(state)) renderRoom();
};
const turnTransition = new TurnTransition(matchMedia('(prefers-reduced-motion: reduce)').matches);
let announcedTurn = '';
let lastUiActive = '';
// Flash loaded each symbol timeline as one package. Keep every UI frame
// decoded before switching the visible image, including slow CDN routes.
const uiFrameReady = (path: string) => {
  const image = battleAssets.getImage(path);
  return Boolean(image?.complete && image.naturalWidth);
};
const props: Array<{ id: PropId; label: string; key: string; cost: number; detail: string }> = [
  { id: 'trident', label: '三叉戟', key: '1', cost: PROP_DEFINITIONS.trident.energy, detail: '一轮发射三枚弹药' },
  { id: 'extra2', label: '追加两次', key: '2', cost: PROP_DEFINITIONS.extra2.energy, detail: '连续发射三轮弹药' },
  { id: 'damage50', label: '伤害 +50%', key: '3', cost: PROP_DEFINITIONS.damage50.energy, detail: '提升本次攻击伤害' },
  { id: 'damage40', label: '伤害 +40%', key: '4', cost: PROP_DEFINITIONS.damage40.energy, detail: '提升本次攻击伤害' },
  { id: 'damage30', label: '伤害 +30%', key: '5', cost: PROP_DEFINITIONS.damage30.energy, detail: '提升本次攻击伤害' },
  { id: 'damage20', label: '伤害 +20%', key: '6', cost: PROP_DEFINITIONS.damage20.energy, detail: '提升本次攻击伤害' },
  { id: 'damage10', label: '伤害 +10%', key: '7', cost: PROP_DEFINITIONS.damage10.energy, detail: '提升本次攻击伤害' },
  { id: 'extra1', label: '追加一次', key: 'z', cost: PROP_DEFINITIONS.extra1.energy, detail: '连续发射两轮弹药' },
  { id: 'fly', label: '纸飞机', key: 'x', cost: PROP_DEFINITIONS.fly.energy, detail: '飞往弹药落点' },
  { id: 'ice', label: '冰冻弹', key: 'c', cost: PROP_DEFINITIONS.ice.energy, detail: '冰冻被命中的对手' },
];

app.innerHTML = classicStage(assets, rawData, props, savedNickname(), muted);
function resizeStage() {
  const scale = Math.min(1.6, innerWidth / 1000, innerHeight / 600);
  $('.game-shell').style.transform = `scale(${scale})`;
  $('.stage-viewport').style.width = `${1000 * scale}px`;
  $('.stage-viewport').style.height = `${600 * scale}px`;
  renderer?.resize();
}
window.addEventListener('resize', resizeStage);
resizeStage();

const connection = new RoomConnection({
  state: receiveState,
  status: setConnectionStatus,
  error: (message) => {
    renderer?.cancelPrediction(connection.playerId); gameAudio.cancelPreview(connection.playerId);
    pendingAimSeq = -1;
    angle = self()?.angle ?? angle;
    visualAngle.set(angle, performance.now(), true);
    renderer?.setAim(connection.playerId, angle);
    updateControls(); toast(message);
  },
  hello: () => connection.send({ type: 'join', name: savedNickname() }),
  clock: (serverNow) => { clockOffset = serverNow - Date.now(); },
});

function send(command: ClientCommand) {
  const sent = connection.send(command);
  if (sent && command.type === 'move') renderer?.predictMove(connection.playerId, command.dx, connection.commandSeq);
  if (sent && command.type === 'aim') pendingAimSeq = connection.commandSeq;
  return sent;
}
function self() { return state?.players.find((p) => p.id === connection.playerId); }
function canManageRoom() { const me = self(); return connected && Boolean(me && !me.bot && me.connected); }
function canAct() {
  const now = Date.now() + clockOffset, presented = turnTransition.sample(now);
  return connected && state?.phase === 'playing' && state.turn.playerId === connection.playerId && self()?.alive
    && now >= state.turn.readyAt && now < state.turn.deadline && sameControlTurn(state, presented)
    && !renderer?.loading && !renderer?.animating && !presented?.inputLocked;
}
function weapon(player = self()): WeaponAsset | undefined { return assets.weapons.find((w) => String(w.id) === String(player?.weaponId)) ?? assets.weapons[0]; }
function toast(message: string) { $('#toast').textContent = message; $('#toast').classList.remove('hidden'); clearTimeout(toastTimeout); toastTimeout = setTimeout(() => $('#toast').classList.add('hidden'), 3500); }

function showEntry() {
  stopHallPolling();
  state = null;
  latestShot = null;
  previousShotId = '';
  lastTurn = '';
  clockOffset = 0;
  keys.clear();
  blockedKeys.clear();
  stopCharge();
  angle = 45;
  visualAngle.set(angle, performance.now(), true);
  power = 0;
  referencePower = recordedPowerWidth = previousPowerWidth = 0;
  presentedPowerTurn = '';
  selectedProps = [];
  usePow = false;
  moveTick = 0;
  aimCadence.reset();
  pendingAimSeq = -1;
  resultPresentation.reset(); presentedResult = null; resultMovieKey = '';
  announceResult = false; announcedResult = '';
  lastSelfAim = null; angerFrom = angerTarget = angerStarted = 0;
  gameAudio.setBattleMusic(null);
  gameAudio.reset();
  presentedLife.clear(); presentedLifeRound = -1;
  turnTransition.reset();
  announcedTurn = '';
  lastUiActive = '';
  if (renderer) { renderer.onAssetReady = undefined; renderer.destroy(); renderer = null; }
  currentScreen = 'entry';
  $('#entry').classList.remove('hidden');
  $('#hall').classList.add('hidden');
  $('#room').classList.add('hidden');
  $('#room').innerHTML = '';
  $('#battle').classList.add('hidden');
  $('#leave-room').classList.add('hidden');
  $('#end-battle').classList.add('hidden');
  $('#result').classList.add('hidden');
  $('#result').innerHTML = '';
  $('#modal').classList.add('hidden');
  clearTimeout(toastTimeout);
  $('#toast').classList.add('hidden');
  updateEntryLoading();
}

function stopHallPolling() {
  if (hallTimer) clearInterval(hallTimer);
  hallTimer = null; hallRequest++; hallLoading = false;
}

function renderHall() {
  if (currentScreen !== 'hall' && currentScreen !== 'joining') return;
  const oldInput = document.querySelector<HTMLInputElement>('#hall-room-name');
  const focused = document.activeElement === oldInput;
  if (oldInput) hallDraft = oldInput.value;
  const selection = oldInput ? [oldInput.selectionStart, oldInput.selectionEnd] as const : null;
  $('#hall').innerHTML = classicHall(hallRooms, assets, (hallLoading && !hallRooms.length) || hallCreating || currentScreen === 'joining', hallError);
  const input = $('#hall-room-name') as HTMLInputElement;
  input.value = hallDraft;
  if (focused && !input.disabled) { input.focus(); if (selection) input.setSelectionRange(selection[0], selection[1]); }
  input.oninput = () => { hallDraft = input.value; };
  $('#refresh-rooms').onclick = () => { void refreshHall(); };
  $('#hall').querySelectorAll<HTMLButtonElement>('[data-enter-room]').forEach((button) => {
    button.onclick = () => enterRoom(button.dataset.enterRoom!);
  });
  $('#create-room-form').onsubmit = (event) => { event.preventDefault(); void createRoom(input.value); };
}

async function refreshHall() {
  if (currentScreen !== 'hall' || hallLoading || hallCreating) return;
  const request = ++hallRequest; hallLoading = true; renderHall();
  try {
    const response = await fetch('/api/rooms', { cache: 'no-store', signal: AbortSignal.timeout(10000) });
    const result = await response.json() as { rooms?: RoomSummary[]; error?: string };
    if (!response.ok || !Array.isArray(result.rooms)) throw new Error(result.error ?? '暂时无法读取房间列表');
    if (request !== hallRequest || currentScreen !== 'hall') return;
    hallRooms = result.rooms; hallError = '';
  } catch (error) {
    if (request === hallRequest && currentScreen === 'hall') hallError = error instanceof Error ? error.message : '房间列表载入失败，点击刷新重试';
  } finally {
    if (request === hallRequest && currentScreen === 'hall') { hallLoading = false; renderHall(); }
  }
}

function showHall() {
  showEntry(); selectedRoom = null; hallError = ''; hallCreating = false;
  currentScreen = 'hall'; $('#entry').classList.add('hidden'); $('#hall').classList.remove('hidden');
  renderHall(); void refreshHall();
  hallTimer = setInterval(() => { if (currentScreen === 'hall') void refreshHall(); }, 5000);
}

function enterRoom(roomId: string, summary?: RoomSummary) {
  if (!globalReady) { toast('游戏素材正在载入，请稍候'); return; }
  if (!validRoomId(roomId) || !hallNickname) { toast('房间链接无效，请从大厅选择房间'); return; }
  const room = summary ?? hallRooms.find((item) => item.id === roomId);
  if (room && (room.available === false || room.playerCount >= 6 || room.phase === 'playing')) {
    hallError = room.playerCount >= 6 ? '房间已满，请选择其他房间' : '这个房间暂时不能加入'; renderHall(); return;
  }
  stopHallPolling(); selectedRoom = { id: roomId, name: room?.name ?? '弹友的房间' };
  currentScreen = 'joining'; hallError = ''; renderHall();
  connection.connect(hallNickname, roomId);
}

async function createRoom(name: string) {
  name = name.trim().slice(0, 20);
  if (!name || currentScreen !== 'hall' || hallCreating || !globalReady) return;
  const request = ++hallRequest; hallLoading = false; hallCreating = true; renderHall();
  try {
    const response = await fetch('/api/rooms', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }), signal: AbortSignal.timeout(10000) });
    const result = await response.json() as { room?: RoomSummary; error?: string };
    if (!response.ok || !result.room || !validRoomId(result.room.id)) throw new Error(result.error ?? '暂时无法创建房间');
    if (request !== hallRequest || currentScreen !== 'hall') return;
    hallCreating = false; hallDraft = ''; enterRoom(result.room.id, result.room);
  } catch (error) {
    if (request === hallRequest && currentScreen === 'hall') { hallCreating = false; hallError = error instanceof Error ? error.message : '创建房间失败，请重试'; renderHall(); }
  }
}

function updateEntryLoading() {
  const visual = battleAssets.statusAll(), audio = gameAudio.statusAll();
  const total = visual.total + audio.total, loaded = visual.loaded + audio.loaded;
  $('#entry-load-fill').style.width = `${total ? loaded / total * 100 : 0}%`;
  $('#entry-load-count').textContent = `${loaded} / ${total}`;
  $('#entry-load-text').textContent = globalReady ? '加载完成' : preloadFailed ? '加载失败，请重试' : '正在加载…';
  ($('#join-form button[type="submit"]') as HTMLButtonElement).disabled = !globalReady;
  $('#entry-retry').classList.toggle('hidden', !preloadFailed);
  ($('#entry-retry') as HTMLButtonElement).disabled = globalPreload !== null;
}

function preloadGame() {
  if (globalPreload) return;
  preloadFailed = false;
  globalPreload = Promise.resolve().then(() => {
    void resourcePacks.preloadAll();
    return Promise.all([battleAssets.warmAll(), gameAudio.preloadAll()]);
  }).then(([visual, audio]) => {
    globalReady = visual && audio && battleAssets.statusAll().ready && gameAudio.statusAll().ready;
    if (globalReady) { refreshPackedAssetBindings(); applyClassicAssetStyles(); }
    preloadFailed = !globalReady;
  }).catch(() => { preloadFailed = true; }).finally(() => { globalPreload = null; updateEntryLoading(); });
  updateEntryLoading();
}

function setConnectionStatus(status: ConnectionStatus) {
  connected = status === 'connected';
  const labels: Record<ConnectionStatus, string> = { connecting: '正在入房…', connected: '已连接', reconnecting: '正在重连…', offline: '尚未入房' };
  $('#connection').className = `connection ${status}`;
  $('#connection').textContent = labels[status];
  $('#connection').title = labels[status];
  $('#connection').setAttribute('aria-label', labels[status]);
  if (status === 'offline') { if (hallNickname) showHall(); else showEntry(); }
  if (status === 'reconnecting') stopCharge();
  if (state?.phase === 'lobby') renderRoom();
  ($('#end-battle') as HTMLButtonElement).disabled = !canManageRoom();
  document.querySelectorAll<HTMLButtonElement>('#rematch, #back-room').forEach((button) => { button.disabled = !canManageRoom(); });
}

function receiveState(next: GameState, shot?: ShotResult, serverTime?: number, ackSeq?: number) {
  if (serverTime) clockOffset = serverTime - Date.now();
  turnTransition.observe(next, shot, serverTime ?? Date.now() + clockOffset);
  const previousState = state;
  state = next;
  if (next.round !== previousState?.round) lastSelfAim = null;
  if (next.round !== previousState?.round || next.phase === 'lobby') {
    referencePower = recordedPowerWidth = previousPowerWidth = power = 0;
    presentedPowerTurn = '';
  }
  if (next.phase === 'finished' && previousState?.phase === 'playing') announceResult = true;
  if (next.phase === 'lobby') {
    gameAudio.setBattleMusic(null);
    announceResult = false; announcedResult = '';
    if (previousState && previousState.phase !== 'lobby') gameAudio.reset();
  }
  if (next.phase === 'playing' && previousState?.phase === 'playing') {
    for (const player of next.players) {
      const before = previousState.players.find(p => p.id === player.id);
      if (before) {
        for (const prop of player.queuedProps ?? []) if (!before.queuedProps?.includes(prop)) {
          gameAudio.propUse(); renderer?.useProp(player.id, prop);
        }
        if (player.queuedPow && !before.queuedPow) { gameAudio.propUse(true); renderer?.useProp(player.id, 'pow'); }
      }
    }
  }
  if (shot || next.lastShot) latestShot = shot ?? next.lastShot;
  if (next.phase === 'lobby') latestShot = null;
  resultPresentation.observe(next, connection.playerId, latestShot);
  if (shot || next.phase !== 'playing') stopCharge();
  const me = self();
  if (shot?.playerId === connection.playerId && me) lastSelfAim = { angle: shot.angle, face: me.face, bodyAngle: shot.bodyAngle ?? renderer?.playerBodyAngle(connection.playerId) ?? 0 };
  if (shot?.playerId === connection.playerId) recordedPowerWidth = Math.ceil(498 * shot.power / 100);
  selectedProps = [...(me?.queuedProps ?? [])];
  usePow = Boolean(me?.queuedPow);
  const newTurn = `${next.round}:${next.turn.number}:${next.turn.playerId}`;
  const aimAcknowledged = pendingAimSeq < 0 || (ackSeq !== undefined && ackSeq >= pendingAimSeq);
  if (me && (newTurn !== lastTurn || (aimAcknowledged && !charging && !keys.has('ArrowUp') && !keys.has('ArrowDown')))) {
    angle = me.angle;
    visualAngle.set(angle, performance.now(), newTurn !== lastTurn);
    renderer?.setAim(connection.playerId, angle);
  }
  if (aimAcknowledged || newTurn !== lastTurn || next.phase !== 'playing') pendingAimSeq = -1;
  if (newTurn !== lastTurn) {
    lastTurn = newTurn;
    for (const key of keys) blockedKeys.add(key);
    keys.clear();
    aimCadence.reset();
    stopCharge();
  }
  $('#entry').classList.add('hidden');
  $('#hall').classList.add('hidden');
  $('#room').classList.toggle('hidden', next.phase !== 'lobby');
  $('#battle').classList.toggle('hidden', next.phase === 'lobby');
  $('#leave-room').classList.remove('hidden');
  $('#end-battle').classList.toggle('hidden', next.phase !== 'playing');
  ($('#end-battle') as HTMLButtonElement).disabled = !canManageRoom();
  if (next.phase === 'lobby') {
    void battleAssets.warm(next);
    renderRoom();
    if (renderer) { renderer.destroy(); renderer = null; }
    currentScreen = 'lobby';
  } else {
    if (!renderer) {
      renderer = new BattleRenderer($('#world'), $('#minimap'), assets, connection.playerId, battleAssets);
      renderer.onAssetReady = () => $('#loading-terrain').classList.toggle('hidden', !renderer?.loading);
    }
    renderer.setState(next, shot ?? next.lastShot ?? undefined, serverTime, ackSeq);
    if (shot && shot.id !== previousShotId) {
      previousShotId = shot.id;
      gameAudio.shot(shot, Date.now() + clockOffset);
    }
    renderBattle(); currentScreen = 'battle';
  }
  if (next.phase === 'playing') gameAudio.setBattleMusic(next.mapId);
}

function renderRoom() {
  if (!state) return;
  const me = self(); const controlsEnabled = canManageRoom();
  $('#room').innerHTML = classicRoom(state, me, assets, controlsEnabled);
  $('#room-label').textContent = selectedRoom?.name ?? '当前房间';
  $('#room').querySelectorAll<HTMLButtonElement>('[data-team]').forEach((b) => b.onclick = () => send({ type: 'team', team: Number(b.dataset.team) as 0 | 1 }));
  $('#room').querySelectorAll<HTMLButtonElement>('[data-add-bot]').forEach((b) => b.onclick = () => send({ type: 'addBot', team: Number(b.dataset.addBot) as 0 | 1 }));
  $('#room').querySelectorAll<HTMLButtonElement>('[data-remove-bot]').forEach((b) => b.onclick = () => send({ type: 'removeBot', playerId: b.dataset.removeBot! }));
  $('#room').querySelectorAll<HTMLButtonElement>('[data-map]').forEach((b) => b.onclick = () => send({ type: 'map', mapId: b.dataset.map! }));
  $('#room').querySelectorAll<HTMLButtonElement>('[data-weapon]').forEach((b) => b.onclick = () => send({ type: 'weapon', weaponId: b.dataset.weapon! }));
  $('#room').querySelectorAll<HTMLButtonElement>('[data-edit-bot]').forEach((button) => button.onclick = () => {
    const bot = state?.players.find(p => p.id === button.dataset.editBot);
    if (!bot) return;
    showModal(`${escape(bot.name)} · 武器`, `<div class="weapon-grid ai-weapons">${assets.weapons.map(w => `<button data-bot-weapon="${escape(w.id)}" data-bot-id="${escape(bot.id)}" title="${escape(w.name)}"><img src="${escape(assetUrl(w.icon))}" alt="${escape(w.name)}"></button>`).join('')}</div>`);
  });
  const resources = battleAssets.statusFor(state);
  const readyButton = $('#ready') as HTMLButtonElement;
  readyButton.disabled = !controlsEnabled || (!me?.ready && !resources.ready && !resources.failed.length);
  const startButton = $('#start') as HTMLButtonElement;
  startButton.disabled ||= !resources.ready;
  if (!resources.ready) $('.room-ready-count').textContent = resources.failed.length ? '素材载入失败，点击准备重试' : '正在载入战斗资源…';
  readyButton.onclick = async () => {
    if (self()?.ready) { send({ type: 'ready', ready: false }); return; }
    if (!state || !canManageRoom()) return;
    const key = roomAssetKey(state);
    if (!await battleAssets.warm(state)) { if (state?.phase === 'lobby') renderRoom(); return; }
    if (state?.phase === 'lobby' && key === roomAssetKey(state)) send({ type: 'ready', ready: true, assetKey: key });
  };
  startButton.onclick = () => { if (state && battleAssets.readyFor(state)) send({ type: 'start' }); };
}

function renderBattle() {
  if (!state) return;
  const displayed = renderer?.presentedPlayers ?? state.players;
  const hud = battleHudPlayers(state, displayed, connection.playerId, turnTransition.sample(Date.now() + clockOffset));
  const active = hud.actor;
  const rosterDivider = new Set(state.players.map((p) => p.team)).size === 1 ? '' : '<span class="roster-versus">VS</span>';
  $('#roster').innerHTML = [0, 1].map((team) => `<div class="roster-team team-${team}">${displayed.filter((p) => p.team === team).map((p) => `<div data-roster-id="${escape(p.id)}" class="roster-player ${p.id === active?.id ? 'active' : ''} ${!p.alive ? 'defeated' : ''}"><div class="roster-player-line"><span>${p.bot ? '◇ ' : ''}${escape(p.name)}</span><small>${p.alive ? Math.ceil(p.hp) : '出局'}</small></div><div class="roster-hp"><i style="width:${Math.max(0, p.hp / p.maxHp * 100)}%"></i></div></div>`).join('')}</div>`).join(rosterDivider);
  $('#turn-number').textContent = `第 ${hud.number} 回合`;
  updateActiveHud(displayed);
  updatePlayerMeters(hud.self);
  $('#minimap-name').textContent = assets.maps.find((m) => String(m.id) === String(state!.mapId))?.name ?? '';
  if (state.phase === 'finished') {
    presentResult();
  } else $('#result').classList.add('hidden');
  updateControls();
}

function updateActiveHud(displayed: readonly PlayerState[] = renderer?.presentedPlayers ?? state?.players ?? []) {
  if (!state) return;
  const { actor, actorId } = battleHudPlayers(state, displayed, connection.playerId, turnTransition.sample(Date.now() + clockOffset));
  const person = $('.hud-person');
  person.dataset.playerId = actorId; person.dataset.team = String(actor?.team ?? 0);
  $('#hud-name').textContent = actor?.name ?? '观战中';
  for (const team of [0, 1]) $('#hud-name').classList.toggle(`team-${team}`, actor?.team === team);
  const w = actor ? weapon(actor) : undefined;
  $('#hud-weapon').textContent = w?.name ?? '';
  const character = assets.characters[actor?.team ?? 0] ?? assets.characters[0];
  const portrait = assetUrl(String(character?.roomPortrait ?? (typeof character?.idle === 'string' ? character.idle : character?.idle?.[0] ?? '')));
  if ($('#hud-character').getAttribute('src') !== portrait) $('#hud-character').setAttribute('src', portrait);
  const held = w?.held ? assetUrl(w.held) : '';
  $('#hud-held').classList.toggle('hidden', !held);
  if (held && $('#hud-held').getAttribute('src') !== held) $('#hud-held').setAttribute('src', held);
  $('#actor-hp-fill').style.width = `${actor ? Math.max(0, Math.min(100, actor.hp / actor.maxHp * 100)) : 0}%`;
  $('#actor-hp-text').textContent = actor ? `${Math.ceil(actor.hp)} / ${actor.maxHp}` : '';
}

function renderResult() {
  if (!state || state.phase !== 'finished') return;
  const me = self(); const won = state.winner === me?.team;
  $('#result').classList.remove('hidden');
  const resultKey = `${state.round}:${state.winner}`;
  if (announceResult && resultKey !== announcedResult) { announcedResult = resultKey; gameAudio.result(won, state.winner === 'draw'); }
  $('#result').innerHTML = `<div class="result-panel"><div class="result-seal">${state.winner === 'draw' ? '平' : won ? '胜' : '负'}</div><h1>${state.winner === 'draw' ? '平局' : `${state.winner === 0 ? '红色' : '蓝色'}战队获胜`}</h1><div class="result-players">${state.players.map((p) => `<div><span class="result-team-dot team-${p.team}"></span><strong>${escape(p.name)}</strong><span>${p.alive ? `剩余 ${Math.ceil(p.hp)} 生命` : '出局'}</span></div>`).join('')}</div><button id="back-room" class="primary-button" ${!canManageRoom() ? 'disabled' : ''}>返回准备房间</button></div>`;
  $('#back-room').onclick = () => send({ type: 'rematch' });
}

/** GameOverAction waits for all physical actions, then plays 75 source ticks
 * before opening the settlement. Original decoration is predecoded; the
 * unavailable overseas title is replaced with plain Chinese typography. */
function presentResult() {
  const frame = resultPresentation.sample(Date.now() + clockOffset, Boolean(renderer?.loading || renderer?.resultAnimating));
  presentedResult = frame;
  const overlay = $('#result');
  if (!frame || frame.stage === 'waiting') { overlay.classList.add('hidden'); return; }
  if (frame.stage === 'settlement') {
    if (overlay.dataset.resultStage !== 'settlement' || overlay.dataset.resultKey !== frame.key) {
      overlay.dataset.resultStage = 'settlement'; overlay.dataset.resultKey = frame.key; renderResult();
    }
    return;
  }
  if (announceResult && frame.key !== announcedResult) {
    announcedResult = frame.key; gameAudio.result(frame.outcome === 'win', frame.outcome === 'draw');
  }
  if (resultMovieKey !== frame.key || overlay.dataset.resultStage !== 'movie') {
    resultMovieKey = frame.key;
    overlay.innerHTML = '<canvas id="result-movie" width="984" height="561" aria-hidden="true"></canvas><strong id="result-title" class="battle-result-title"></strong>';
    overlay.dataset.resultStage = 'movie'; overlay.dataset.resultKey = frame.key;
  }
  overlay.classList.remove('hidden');
  const title = $('#result-title');
  title.classList.toggle('is-loss', frame.outcome === 'loss');
  if (frame.movie) {
    const movie = frame.movie, image = battleAssets.getImage(movie.path);
    if (image) {
      const ctx = ($('#result-movie') as HTMLCanvasElement).getContext('2d')!;
      ctx.clearRect(0, 0, 984, 561);
      const destination = { x: frame.position.x - 8 - movie.anchor.x, y: frame.position.y - 8 - movie.anchor.y, width: movie.width, height: movie.height };
      uiSprite.draw(ctx, { image, source: movie.crop, destination }, { image, source: movie.nextCrop, destination }, movie.mix);
    }
    title.textContent = movie.title.label;
    title.style.left = `${frame.position.x - 8 + movie.title.rect.x}px`;
    title.style.top = `${frame.position.y - 8 + movie.title.rect.y}px`;
    title.style.width = `${movie.title.rect.width}px`;
    title.style.height = title.style.lineHeight = `${movie.title.rect.height}px`;
    title.style.opacity = String(movie.title.opacity);
  } else {
    title.textContent = '平局'; title.style.cssText = 'left:349px;top:164px;width:210px;height:66px;line-height:66px;opacity:1';
  }
}

function updatePlayerMeters(viewed?: PlayerState) {
  if (!viewed) return;
  $('#hp-fill').style.width = `${Number($('#hp-fill').dataset.fillWidth) * Math.max(0, Math.min(1, viewed.hp / viewed.maxHp))}px`;
  $('#hp-text').textContent = `生命 ${Math.ceil(viewed.hp)} / ${viewed.maxHp}`;
  const maxEnergy = 240 + Math.floor(Number(weapon(viewed)?.agility ?? 0) / 30);
  $('#energy-fill').style.width = `${Number($('#energy-fill').dataset.fillWidth) * Math.max(0, Math.min(1, viewed.energy / maxEnergy))}px`;
  $('#energy-text').textContent = `体力 ${viewed.energy}`;
  const now = performance.now(), fraction = angerFrom + (angerTarget - angerFrom) * Math.min(1, (now - angerStarted) / 300);
  const target = Math.max(0, Math.min(1, viewed.anger / 200));
  if (target !== angerTarget) { angerFrom = fraction; angerTarget = target; angerStarted = now; }
  $('#anger-fill').style.clipPath = angerSector(fraction);
  $('#anger-text').textContent = `怒气 ${viewed.anger} / 200`;
  $('#pow').title = `怒气 ${viewed.anger} / 200 · 满怒可使用必杀 [B]`;
}

function updateControls(displayed?: readonly PlayerState[]) {
  const enabled = Boolean(canAct()); const me = self();
  document.querySelectorAll<HTMLButtonElement>('[data-prop]').forEach((button) => {
    const id = button.dataset.prop as PropId;
    button.classList.toggle('selected', selectedProps.includes(id));
    const exclusive = selectedProps.some(p => p === 'fly' || p === 'ice');
    const incompatible = exclusive || ((id === 'fly' || id === 'ice') && (selectedProps.length > 0 || usePow)) || (id === 'trident' && usePow);
    button.disabled = !enabled || selectedProps.includes(id) || selectedProps.length >= 3 || incompatible || (props.find(p => p.id === id)?.cost ?? 0) > (me?.energy ?? 0);
  });
  const pow = $('#pow') as HTMLButtonElement;
  pow.disabled = !enabled || usePow || selectedProps.some(p => p === 'trident' || p === 'fly' || p === 'ice') || (me?.anger ?? 0) < 200;
  pow.classList.toggle('available', (me?.anger ?? 0) >= 200);
  pow.classList.toggle('selected', usePow);
  ($('#skip') as HTMLButtonElement).disabled = !enabled;
  ($('#fire') as HTMLButtonElement).disabled = state?.phase !== 'playing';
  ($('#angle-up') as HTMLButtonElement).disabled = !enabled;
  ($('#angle-down') as HTMLButtonElement).disabled = !enabled;
  $('#fire').classList.toggle('charging', charging);
  const face = renderer?.debug.movement.find(p => p.playerId === connection.playerId)?.predicted.face ?? me?.face ?? 1;
  const bodyAngle = renderer?.playerBodyAngle(connection.playerId) ?? 0;
  const displayedAim = gaugeAim(visualAngle.sample(performance.now()), face, bodyAngle, lastSelfAim);
  $('#angle-value').innerHTML = `${gaugeAim(angle, face, bodyAngle, lastSelfAim).value}<small>°</small>`;
  $('#angle-sub').style.transform = `rotate(${bodyAngle}deg)`;
  const flying = selectedProps.includes('fly'), w = weapon();
  const minAngle = flying ? 0 : w?.minAngle ?? 0, maxAngle = flying ? 90 : w?.maxAngle ?? 90;
  $('#angle-needle').style.transform = `rotate(${displayedAim.needle}deg)`;
  $('#angle-needle').classList.toggle('hidden', displayedAim.overlap);
  $('#angle-overlap').classList.toggle('hidden', !displayedAim.overlap);
  $('#angle-overlap').style.transform = `rotate(${displayedAim.needle}deg)`;
  $('#angle-sector').style.clipPath = angleSector(minAngle, maxAngle, face);
  $('#angle-sector').classList.toggle('hidden', flying);
  $('#angle-green').classList.toggle('hidden', !flying);
  $('#angle-green').style.clipPath = angleSector(0, 90, face);
  $('#angle-clone').classList.toggle('hidden', !lastSelfAim || displayedAim.overlap);
  if (displayedAim.clone !== null) $('#angle-clone').style.transform = `rotate(${displayedAim.clone}deg)`;
  const powReady = $('#pow-ready') as HTMLCanvasElement;
  powReady.classList.toggle('hidden', (me?.anger ?? 0) < 200 || usePow);
  if ((me?.anger ?? 0) >= 200 && !usePow) {
    drawUiMovie(powReady, danderReadySample(performance.now()));
  }
  const visualWidth = charging ? 498 * chargeVisualPower(performance.now() - chargeStarted) / 100 : Math.ceil(498 * power / 100);
  $('#power-fill').style.transform = `scaleX(${visualWidth / 498})`;
  $('#power-previous').style.width = `${previousPowerWidth}px`;
  $('#power-marker').style.left = `${referencePower}%`;
  $('#power-number').textContent = `${Math.round(power)} / 100`;
  $('#power-number').classList.toggle('hidden', power === 0 && !charging);
  const now = Date.now() + clockOffset;
  const settling = state?.phase === 'playing' && now < state.turn.readyAt;
  const visibleSelf = (displayed ?? renderer?.presentedPlayers)?.find(player => player.id === connection.playerId) ?? me;
  $('#battle-notice').textContent = renderer?.loading ? '正在进入战场…' : !connected ? '连接正在恢复…' : visibleSelf && !visibleSelf.alive && state?.phase === 'playing' ? '你已出局' : '';
  $('#battle-notice').classList.toggle('your-turn', enabled);
  if (state?.phase === 'playing') {
    const remaining = Math.max(0, Math.ceil((state.turn.deadline - now) / 1000));
    const countdown = settling ? '' : String(remaining);
    const timer = $('#turn-timer');
    if (timer.dataset.value !== countdown) {
      timer.dataset.value = countdown;
      timer.innerHTML = [...countdown].map(digit => {
        const key = `turn${remaining <= 4 ? 'Red' : 'Blue'}${digit}` as keyof typeof classicUi.assets;
        const asset = classicUi.assets[key];
        return 'path' in asset && uiFrameReady(asset.path) ? `<img src="${assetUrl(asset.path)}" alt="${digit}">` : digit;
      }).join('');
    }
    $('#turn-timer').classList.toggle('urgent', remaining <= 10 && !settling);
  }
}

function presentTurn(animationNow: number) {
  const frame = turnTransition.sample(Date.now() + clockOffset, connection.playerId);
  updateActiveHud();
  if (!frame) return;
  if (frame.key !== presentedPowerTurn) {
    // EnergyView.beginNewTurn runs after ChangePlayerAction waits for moving
    // objects. Keep the fired gold bar until the displayed turn changes.
    previousPowerWidth = recordedPowerWidth;
    power = 0;
    // The original hand-off resets the keyboard after moving objects finish.
    // A key pressed during that wait must also be released before controlling
    // the newly presented living.
    for (const key of keys) blockedKeys.add(key);
    keys.clear();
    aimCadence.reset();
    stopCharge();
    visualAngle.set(angle, animationNow, true);
    presentedPowerTurn = frame.key;
  }
  const banner = $('#turn-banner');
  banner.classList.toggle('hidden', !frame.visible || !frame.isSelf);
  banner.classList.toggle('self-turn', frame.isSelf);
  banner.style.opacity = String(frame.opacity);
  banner.style.transform = `translateX(${frame.translateX}px) scale(${frame.scale})`;
  if (frame.visible && frame.isSelf) {
    drawUiMovie($('#turn-glow') as HTMLCanvasElement, turnGlowSample(frame.elapsed), { x: 0, y: 4.5, width: 565, height: 75 });
  }
  $('#turn-name').textContent = frame.isSelf ? '轮到你了' : `${frame.name} 的回合`;
  if (frame.visible && frame.key !== announcedTurn) { announcedTurn = frame.key; gameAudio.turn(frame.isSelf); }
  $('#wind-value').textContent = Math.abs(frame.wind).toFixed(1);
  $('#wind-direction').classList.toggle('wind-left', frame.wind < 0);
  $('#previous-wind').textContent = frame.previousWind == null ? '' : `${frame.previousWind < 0 ? '←' : '→'} ${Math.abs(frame.previousWind).toFixed(1)}`;
  drawUiMovie($('#wind-arrow') as HTMLCanvasElement, windArrowSample(animationNow));
  $('#turn-number').textContent = `第 ${frame.number} 回合`;
  if (lastUiActive !== frame.playerId) {
    lastUiActive = frame.playerId;
    document.querySelectorAll<HTMLElement>('[data-roster-id]').forEach(el => el.classList.toggle('active', el.dataset.rosterId === frame.playerId));
  }
}

function drawUiMovie(canvas: HTMLCanvasElement, sample: { path: string; nextPath: string; mix: number }, destination?: { x: number; y: number; width: number; height: number }) {
  const current = battleAssets.getImage(sample.path), next = battleAssets.getImage(sample.nextPath);
  if (!current) return;
  const ctx = canvas.getContext('2d')!;
  const logicalWidth = Number(canvas.dataset.logicalWidth) || canvas.width;
  const logicalHeight = Number(canvas.dataset.logicalHeight) || canvas.height;
  destination ??= { x: 0, y: 0, width: logicalWidth, height: logicalHeight };
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.setTransform(canvas.width / logicalWidth, 0, 0, canvas.height / logicalHeight, 0, 0);
  uiSprite.draw(ctx, { image: current, destination }, next ? { image: next, destination } : null, sample.mix);
}

function changeAngle(delta: number) {
  if (!canAct()) return;
  const previousAngle = angle;
  const w = weapon(), flying = selectedProps.includes('fly');
  angle = Math.max(flying ? 0 : w?.minAngle ?? 0, Math.min(flying ? 90 : w?.maxAngle ?? 90, angle + delta));
  if (angle === previousAngle) return;
  visualAngle.set(angle, performance.now());
  renderer?.setAim(connection.playerId, angle);
  gameAudio.aim();
  send({ type: 'aim', angle }); updateControls();
}
function toggleProp(id: PropId) {
  if (!canAct() || selectedProps.includes(id)) return;
  gameAudio.propActivate();
  send({ type: 'useProp', propId: id });
}
function activatePow() {
  if (!canAct() || usePow) return;
  gameAudio.propActivate();
  send({ type: 'useProp', propId: 'pow' });
}
function beginCharge() { if (!canAct() || charging) return; charging = true; chargeStarted = performance.now(); power = 0; gameAudio.charge(); updateControls(); }
function stopCharge() { charging = false; gameAudio.cancelCharge(); renderer?.cancelShotPreparation(); keys.delete(' '); }
function releaseCharge() {
  if (!charging) return;
  const charge = chargePower(performance.now() - chargeStarted);
  power = charge.power;
  recordedPowerWidth = Math.ceil(498 * power / 100);
  if (charge.expired) { stopCharge(); if (canAct()) send({ type: 'skip' }); renderer?.clearAim(); updateControls(); return; }
  const firedPower = Math.max(1, Math.round(power)); charging = false;
  visualAngle.set(angle, performance.now(), true);
  gameAudio.cancelCharge();
  if (canAct()) {
    gameAudio.releaseCharge();
    const kind = selectedProps.includes('fly') ? 'fly' : selectedProps.includes('ice') ? 'ice' : usePow ? 'special' : 'normal';
    if (send({ type: 'fire', angle, power: firedPower })) {
      renderer?.previewShot(connection.playerId, kind);
      gameAudio.previewShot(connection.playerId, String(weapon()?.id), kind);
    }
    selectedProps = []; usePow = false;
  }
  renderer?.clearAim(); updateControls();
}
function inputLoop(now: number) {
  if (currentScreen === 'battle') {
    presentTurn(now);
    presentResult();
    const presented = renderer?.presentedPlayers ?? state?.players ?? [];
    if (state && presentedLifeRound !== state.round) {
      presentedLife.clear(); presentedLifeRound = state.round;
    }
    for (const player of presented) {
      if (presentedLife.get(player.id) === true && !player.alive) gameAudio.death();
      presentedLife.set(player.id, player.alive);
    }
    updatePlayerMeters(presented.find(p => p.id === connection.playerId));
    for (const player of presented) {
      const roster = document.querySelector<HTMLElement>(`[data-roster-id="${CSS.escape(player.id)}"]`);
      roster?.classList.toggle('defeated', !player.alive);
      const fill = roster?.querySelector<HTMLElement>('.roster-hp i');
      if (fill) fill.style.width = `${Math.max(0, player.hp / player.maxHp * 100)}%`;
      const text = roster?.querySelector<HTMLElement>('.roster-player-line small');
      if (text) text.textContent = player.alive ? String(Math.ceil(player.hp)) : '出局';
    }
    if (renderer?.walkingPlayerIds.length) gameAudio.walk();
    // Falling is a distinct source action; held movement must not accumulate
    // missed horizontal ticks and burst forward when the character lands.
    if (renderer?.falling) moveTick = now;
    if (charging) {
      const charge = chargePower(now - chargeStarted); power = charge.power;
      recordedPowerWidth = Math.ceil(498 * power / 100);
      gameAudio.charge();
      if (charge.expired) { stopCharge(); if (canAct()) send({ type: 'skip' }); }
    }
    if (canAct() && !charging) {
      if (keys.has('ArrowLeft') || keys.has('ArrowRight')) {
        const direction = keys.has('ArrowLeft') && keys.has('ArrowRight') ? walkDirection : keys.has('ArrowLeft') ? -1 : 1;
        const step = walkCadence(moveTick, now);
        if (step.distance) { send({ type: 'move', dx: direction * step.distance }); moveTick = step.tick; }
      }
    }
    if (canAct() && (keys.has('ArrowUp') || keys.has('ArrowDown'))) {
      const steps = aimCadence.advance(now);
      for (let step = 0; step < steps; step++) changeAngle(keys.has('ArrowDown') ? -1 : 1);
    }
    updateControls(presented);
  }
  requestAnimationFrame(inputLoop);
}
requestAnimationFrame(inputLoop);

document.addEventListener('keydown', (event) => {
  if ((event.target as HTMLElement).matches('input,textarea') || !$('#modal').classList.contains('hidden')) return;
  if (currentScreen !== 'battle') return;
  if (blockedKeys.has(event.key)) { event.preventDefault(); return; }
  if ((event.key === 'ArrowUp' || event.key === 'ArrowDown') && canAct()) {
    changeAngle(event.key === 'ArrowDown' ? -1 : 1);
    aimCadence.keyDown(performance.now());
  }
  if (!event.repeat && (event.key === 'ArrowLeft' || event.key === 'ArrowRight') && canAct() && !charging) {
    walkDirection = event.key === 'ArrowLeft' ? -1 : 1;
    send({ type: 'move', dx: walkDirection * ORIGINAL_WALK_STEP });
    moveTick = performance.now();
  }
  if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', ' '].includes(event.key)) { event.preventDefault(); keys.add(event.key); }
  if (event.key === ' ' && !event.repeat) beginCharge();
  if (!event.repeat) {
    const prop = props.find((p) => p.key === event.key.toLowerCase()); if (prop) toggleProp(prop.id);
    if (event.key.toLowerCase() === 'p' && canAct()) send({ type: 'skip' });
    if (event.key.toLowerCase() === 'b') activatePow();
  }
});
document.addEventListener('keyup', (event) => { blockedKeys.delete(event.key); keys.delete(event.key); if (!keys.has('ArrowUp') && !keys.has('ArrowDown')) aimCadence.reset(); if (event.key === ' ') { event.preventDefault(); releaseCharge(); } });
window.addEventListener('blur', () => { keys.clear(); blockedKeys.clear(); aimCadence.reset(); stopCharge(); });
$('#battle').addEventListener('pointerdown', (event) => {
  const button = (event.target as HTMLElement).closest<HTMLElement>('button');
  if (button?.matches('#skip,#pow,#angle-up,#angle-down,[data-prop]') && state?.phase === 'playing') pointerTurns.set(button, turnKey(state));
}, true);
$('#battle').addEventListener('click', (event) => {
  const button = (event.target as HTMLElement).closest<HTMLElement>('button');
  if (button?.matches('#skip,#pow,#angle-up,#angle-down,[data-prop]') && state?.phase === 'playing' && pointerTurns.has(button) && pointerTurns.get(button) !== turnKey(state)) {
    event.preventDefault(); event.stopImmediatePropagation(); pointerTurns.delete(button);
  }
}, true);
// EnergyView.__click moves the reference slider even during another player's
// turn; only Space charges a shot and retains the authoritative input lock.
$('#fire').onclick = (event) => {
  if (state?.phase !== 'playing' || event.detail === 0) return;
  const rect = $('#fire').getBoundingClientRect();
  referencePower = Math.max(0, Math.min(100, (event.clientX - rect.left) / rect.width * 499 / 498 * 100));
  updateControls();
};
$('#angle-up').onclick = () => changeAngle(1);
$('#angle-down').onclick = () => changeAngle(-1);
$('#skip').onclick = () => { if (canAct()) send({ type: 'skip' }); };
$('#pow').onclick = activatePow;
document.querySelectorAll<HTMLButtonElement>('[data-prop]').forEach((button) => button.onclick = () => toggleProp(button.dataset.prop as PropId));
document.querySelectorAll<HTMLButtonElement>('[data-camera]').forEach((button) => button.onclick = () => { const type = button.dataset.camera; if (type === 'in') renderer?.zoom(.25); else if (type === 'out') renderer?.zoom(-.25); else if (type === 'follow') renderer?.followTurn(); else renderer?.resetCamera(); });
$('#join-form').addEventListener('submit', (event) => {
  event.preventDefault(); const name = ($('#nickname') as HTMLInputElement).value.trim().slice(0, 16); if (!name || !globalReady) return;
  if (!muted) gameAudio.activate();
  hallNickname = name; localStorage.setItem('ddt2009.nickname', name); showHall();
  const invitation = invitedRoomId(location.href);
  if (invitation && !invitationUsed) { invitationUsed = true; enterRoom(invitation); }
});
$('#leave-room').onclick = () => connection.close();
$('#invite').onclick = async () => {
  if (!selectedRoom || !state) { toast('先从大厅进入房间，再邀请朋友'); return; }
  const url = roomInviteUrl(location.href, selectedRoom.id);
  try { await navigator.clipboard.writeText(url); toast('房间链接已复制'); }
  catch { showModal('邀请朋友', `<input class="invite-copy" aria-label="房间链接" readonly value="${escape(url)}">`); }
};
$('#sound').onclick = () => { muted = !muted; if (!muted) gameAudio.activate(); else gameAudio.reset(); localStorage.setItem('ddt2009.muted', String(muted)); $('#sound').classList.toggle('muted', muted); $('#sound').title = muted ? '开启声音' : '关闭声音'; $('#sound').setAttribute('aria-label', $('#sound').title); $('#sound').setAttribute('aria-pressed', String(!muted)); toast(muted ? '声音已关闭' : '声音已开启'); };
function showModal(title: string, content: string) { keys.clear(); aimCadence.reset(); stopCharge(); $('#modal').classList.remove('hidden'); $('#modal').innerHTML = `<div class="modal"><button id="close-modal" class="modal-close" aria-label="关闭">×</button><h2>${title}</h2>${content}</div>`; $('#close-modal').onclick = () => $('#modal').classList.add('hidden'); }
$('#modal').addEventListener('click', (event) => { if (event.target === $('#modal')) $('#modal').classList.add('hidden'); });
$('#help').onclick = () => showModal('帮助', '<div class="help-grid"><kbd>← →</kbd><span>移动人物，消耗体力</span><kbd>↑ ↓</kbd><span>调整发射角度</span><kbd>空格</kbd><span>按住蓄力，松开发射；蓄满后力量会回落</span><kbd>1 — 7</kbd><span>使用道具，立即消耗体力</span><kbd>Z X C</kbd><span>追加一次、纸飞机、冰冻弹</span><kbd>B</kbd><span>怒气满时使用必杀技</span><kbd>P</kbd><span>跳过当前回合</span></div>');
$('#end-battle').onclick = () => { if (canManageRoom()) showModal('返回准备房间', '<p>结束本局并返回房间？</p><button id="confirm-end" class="primary-button">结束本局并返回</button>'); };
$('#modal').addEventListener('click', (event) => { if ((event.target as HTMLElement).closest('#confirm-end') && canManageRoom()) { send({ type: 'endBattle' }); $('#modal').classList.add('hidden'); } });
$('#about').onclick = () => {
  const me = self();
  showModal('资料', `<div class="help-grid"><span>昵称</span><strong>${escape(me?.name ?? hallNickname ?? '') || escape(($('#nickname') as HTMLInputElement).value)}</strong>${me ? `<span>武器</span><strong>${escape(weapon(me)?.name ?? '')}</strong>` : ''}</div>`);
};
$('#modal').addEventListener('click', event => {
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-bot-weapon]');
  if (button && canManageRoom()) { send({ type: 'weapon', weaponId: button.dataset.botWeapon!, playerId: button.dataset.botId }); $('#modal').classList.add('hidden'); }
});
function applyClassicAssetStyles() {
for (const [name, item] of Object.entries(classicUi.assets)) {
  if ('path' in item) document.documentElement.style.setProperty(`--asset-${name}`, `url("${assetUrl(item.path)}")`);
  if ('pieces' in item) item.pieces.forEach((piece, index) => {
    document.documentElement.style.setProperty(`--asset-${name}-${index + 1}`, `url("${assetUrl(piece.path)}")`);
  });
}
if (rawData.ui) {
  document.documentElement.style.setProperty('--original-angle', `url("${assetUrl(rawData.ui.angleBack)}"), url("${assetUrl(rawData.ui.angleDial)}")`);
  document.documentElement.style.setProperty('--original-prop', `url("${assetUrl(rawData.ui.propCell)}")`);
  document.documentElement.style.setProperty('--original-power', `url("${assetUrl(rawData.ui.powerBack)}")`);
}
}
applyClassicAssetStyles();
Object.defineProperty(window, '__DDT_DEBUG__', { value: Object.freeze({
  get state() { return state ? JSON.parse(JSON.stringify(state)) : null; },
  get shot() { return latestShot ? JSON.parse(JSON.stringify(latestShot)) : null; },
  get roomAssets() { return state ? battleAssets.statusFor(state) : null; },
  get allAssets() { return { ready: globalReady, visual: battleAssets.statusAll(), audio: gameAudio.statusAll() }; },
  get renderer() { return renderer ? JSON.parse(JSON.stringify(renderer.debug)) : null; },
  get playerId() { return connection.playerId; },
  get connection() { return connection.status; },
  get room() { return selectedRoom ? { ...selectedRoom } : null; },
  get hall() { return { visible: currentScreen === 'hall', loading: hallLoading || hallCreating, rooms: hallRooms.map((room) => ({ ...room })), error: hallError }; },
  get audio() { return gameAudio.debug; },
  get result() { return presentedResult ? JSON.parse(JSON.stringify(presentedResult)) : null; },
  get presentation() { return turnTransition.sample(Date.now() + clockOffset, connection.playerId); },
  get input() { return { angle, visualAngle: visualAngle.sample(performance.now()), power, charging, visualPower: charging ? chargeVisualPower(performance.now() - chargeStarted) : power, chargeElapsedMs: charging ? performance.now() - chargeStarted : null, referencePower, previousPowerWidth, recordedPowerWidth, selectedProps: [...selectedProps], usePow, pendingAimSeq }; },
}), writable: false });

// A saved nickname is prefilled; joining a room still requires an explicit choice.
battleAssets.onProgress = scheduleEntryLoading;
gameAudio.onLoadProgress = scheduleEntryLoading;
$('#entry-retry').onclick = preloadGame;
preloadGame();

app.addEventListener('click', event => {
  const button = (event.target as HTMLElement).closest('button');
  if (button && !button.disabled && (button.closest('#room') || button.closest('.stage-tools') || button.closest('#modal'))) gameAudio.propActivate();
});
